import { useCallback } from 'react'
import { ApolloError, useMutation } from '@apollo/client'
import { AdminAPI, GraphQL } from '@guestgoodie/graphql'
import { useLingui } from '@lingui/react/macro'

import * as Form from '@/features/form'

export type CustomerFormData = {
  email: string
  contactName: string
  companyName: string
  vat: string
  line1: string
  line2: string
  country: string
  postalCode: string
  city: string
}

export type RoomsFormData = {
  rooms: number
  interval: GraphQL.BillingInterval
}

export type FormData = CustomerFormData & RoomsFormData

export function useBillingForm({ onDone }: { onDone: () => void }) {
  const { t } = useLingui()

  const [activateBilling, { loading: loadingActivate }] = useMutation(
    AdminAPI.billing.ACTIVATE,
    {
      refetchQueries: [AdminAPI.organization.GET] // Need to update the cache
    }
  )

  const [updateBilling, { loading: loadingUpdate }] = useMutation(
    AdminAPI.billing.UPDATE,
    {
      refetchQueries: [AdminAPI.organization.GET] // Need to update the cache
    }
  )

  const { errors, parseApolloErrors, setServerError } = Form.useErrorHelpers({})

  const activate = useCallback(
    async (data: FormData) => {
      return activateBilling({
        variables: {
          input: {
            city: data.city,
            companyName: data.companyName,
            contactName: data.contactName,
            country: data.country,
            postalCode: data.postalCode,
            email: data.email,
            interval: data.interval,
            line1: data.line1,
            rooms: data.rooms,
            vat: data.vat,
            line2: data.line2
          }
        }
      })
        .then(({ data, errors }) => {
          if (errors) return Promise.reject(errors)
          if (!data) return Promise.reject('unknown')

          onDone()
        })
        .catch(error => {
          if (error instanceof ApolloError) {
            if (error.message === 'too_many_spaces') {
              setServerError(
                'error-too-many-spaces',
                t({
                  comment:
                    'Error message that is shown to the user when they try to safe their licensing with less rooms than they currently have.',
                  message:
                    'You selected less rooms than you currently have, delete them to proceed.'
                })
              )

              return
            }

            parseApolloErrors(error.graphQLErrors)

            return
          }

          if (Array.isArray(error)) {
            parseApolloErrors(error)

            return
          }

          setServerError(
            'error-billing-update',
            t({
              comment:
                'Generic error message that we show when something unexpected happens.',
              message:
                'Something went wrong, please try again, if this keeps happening please write our support.'
            })
          )
        })
    },
    [onDone, activateBilling, t, parseApolloErrors, setServerError]
  )

  const update = useCallback(
    async (data: FormData) => {
      return updateBilling({
        variables: {
          input: {
            city: data.city,
            companyName: data.companyName,
            contactName: data.contactName,
            country: data.country,
            postalCode: data.postalCode,
            email: data.email,
            interval: data.interval,
            line1: data.line1,
            rooms: data.rooms,
            vat: data.vat,
            line2: data.line2
          }
        }
      })
        .then(({ data, errors }) => {
          if (errors) return Promise.reject(errors)
          if (!data) return Promise.reject('unknown')

          onDone()
        })
        .catch(error => {
          if (error instanceof ApolloError) {
            if (error.message === 'too_many_spaces') {
              setServerError(
                'error-too-many-spaces',
                t({
                  comment:
                    'Error message that is shown to the user when they try to safe their licensing with less rooms than they currently have.',
                  message:
                    'You selected less rooms than you currently have, delete them to proceed.'
                })
              )

              return
            }

            parseApolloErrors(error.graphQLErrors)

            return
          }

          if (Array.isArray(error)) {
            parseApolloErrors(error)

            return
          }

          setServerError(
            'error-billing-update',
            t({
              comment:
                'Generic error message that we show when something unexpected happens.',
              message:
                'Something went wrong, please try again, if this keeps happening please write our support.'
            })
          )
        })
    },
    [onDone, updateBilling, t, parseApolloErrors, setServerError]
  )

  console.log(errors)

  return {
    errors,
    activate,
    update,
    loading: loadingActivate || loadingUpdate
  }
}
